/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  emailLoginConfigs,
  fidpGitHub,
  fidpGoogle,
  fidpMicrosoft,
} from 'config';
import SignupEmbeddedCard from 'modules/signup/SignupEmbeddedCard';
import { getGrowthHackingParams } from 'utils/growthHacking/utm';
import { EMBEDDED_SIGN_UP_HANDLER } from 'utils/route';

function SignupEmbedded() {
  const [queryParams, setQueryParams] = useState(
    new URLSearchParams(window.location.search)
  );
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const modifiedStateParams: { [key: string]: any } = {};
  let isFromContestPage = '';
  let isCodeChallenge = false;
  if (location.pathname.includes('signup_embedded_contest')) {
    isFromContestPage = '&isFromContestPage=true';
    isCodeChallenge = true;
  }

  useEffect(() => {
    const qParams = new URLSearchParams(window.location.search);
    setQueryParams(qParams);
  }, [window.location.search]);

  const growthHackingParams = getGrowthHackingParams([...queryParams]);
  const hasGrowthHackingParams = growthHackingParams.length > 0;
  if (hasGrowthHackingParams) {
    growthHackingParams.forEach(([key, value]) => {
      modifiedStateParams[key] = value;
    });
  }
  const handleEmailSignup = () => {
    const emailSignupUrl =
      `${emailLoginConfigs.ASGARDEO_SIGNUP_URL}?initiated-platform=choreo&template-name=` +
      `${emailLoginConfigs.REGISTER_MAIL_TEMPLATE_NAME}&tenant-url-prefix=${emailLoginConfigs.TENANT_URL_PREFIX}` +
      `&post-register-callback=${emailLoginConfigs.POST_REGISTER_CALLBACK_URL}${isFromContestPage}`;

    window.open(emailSignupUrl, '_blank');
  };

  const handleGithubSignup = () => {
    modifiedStateParams.fidpId = fidpGitHub;
    window.open(
      `${EMBEDDED_SIGN_UP_HANDLER}?fidpId=${fidpGitHub}&stateParamObj=${encodeURIComponent(
        JSON.stringify(modifiedStateParams)
      )}${isFromContestPage}`,
      '_blank'
    );
  };

  const handleGoogleSignup = () => {
    modifiedStateParams.fidpId = fidpGoogle;
    window.open(
      `${EMBEDDED_SIGN_UP_HANDLER}?fidpId=${fidpGoogle}&stateParamObj=${encodeURIComponent(
        JSON.stringify(modifiedStateParams)
      )}${isFromContestPage}`,
      '_blank'
    );
  };

  const handleMicrosoftSignup = () => {
    modifiedStateParams.fidpId = fidpMicrosoft;
    window.open(
      `${EMBEDDED_SIGN_UP_HANDLER}?fidpId=${fidpMicrosoft}&stateParamObj=${encodeURIComponent(
        JSON.stringify(modifiedStateParams)
      )}${isFromContestPage}`,
      '_blank'
    );
  };

  const handleResize = () => {
    if (window.innerWidth < 1000) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
  }, []);

  return (
    <SignupEmbeddedCard
      onEmailClick={handleEmailSignup}
      onGithubClick={handleGithubSignup}
      onGoogleClick={handleGoogleSignup}
      onMicrosoftClick={handleMicrosoftSignup}
      isMobile={isMobile}
      isCodeChallenge={isCodeChallenge}
    />
  );
}

export default SignupEmbedded;
